import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Link from '@cloudscape-design/components/link';

export const AboutUrlSafetyTab = () => {
  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">About Check URL on invisible Characters</Header>}>
        <SpaceBetween size="m">
          <div>
            This tool detects and analyzes invisible characters hidden in URLs. Invisible characters—such as zero-width spaces, directional marks, and other hidden Unicode—can be used to disguise malicious links, bypass security filters, or create deceptive URLs that appear legitimate.
          </div>

          <div>
            <Box variant="h3">Primary Focus: Invisible Character Detection</Box>
            <ul>
              <li><strong>Zero-Width Characters:</strong> Detects Zero-Width Space (U+200B), Zero-Width Non-Joiner (U+200C), Zero-Width Joiner (U+200D), and Word Joiner (U+2060)</li>
              <li><strong>Directional Marks:</strong> Identifies Left-to-Right Mark (U+200E), Right-to-Left Mark (U+200F), and BiDi control characters</li>
              <li><strong>Format Characters:</strong> Detects format control characters, soft hyphens, and other invisible formatting marks</li>
              <li><strong>Security Analysis:</strong> Performs 21+ security pattern checks including steganography, normalization attacks, and character substitution</li>
              <li><strong>Position Tracking:</strong> Shows exact location of each invisible character in the URL</li>
              <li><strong>Visual Highlighting:</strong> Displays invisible characters with clear visual indicators in the analyzed text</li>
            </ul>
          </div>

          <div>
            <Box variant="h3">Additional Security Checks</Box>
            <ul>
              <li><strong>Homograph attacks:</strong> Detects Unicode characters that look like Latin letters (e.g., Cyrillic 'а' vs Latin 'a')</li>
              <li><strong>URL structure analysis:</strong> Basic validation of URL format and components</li>
            </ul>
          </div>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Invisible Character Examples</Header>}>
        <SpaceBetween size="m">
          <ExpandableSection headerText="Zero-Width Space Attack" variant="container">
            <SpaceBetween size="s">
              <div>
                <strong>Attack scenario:</strong> Attackers insert Zero-Width Space (U+200B) characters into URLs to bypass security filters or disguise the true destination.
              </div>
              <div>
                <strong>Example:</strong>{' '}
                {/* eslint-disable-next-line no-irregular-whitespace */}
                <code>https://pay​pal.com/login</code> (contains Zero-Width Space between 'pay' and 'pal')
              </div>
              <div>
                <strong>Why it's dangerous:</strong> The URL looks legitimate but contains hidden characters that can:
                <ul style={{ marginTop: '8px' }}>
                  <li>Bypass email security filters that check for exact domain matches</li>
                  <li>Make copy-paste attacks more effective</li>
                  <li>Create multiple unique fingerprints while appearing identical</li>
                  <li>Evade URL reputation services</li>
                </ul>
              </div>
              <div>
                The tool detects and highlights these hidden characters in the <strong>Analysis Results</strong> section, showing exact position, character type, and security risk assessment.
              </div>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Directional Mark Manipulation" variant="container">
            <SpaceBetween size="s">
              <div>
                <strong>Attack scenario:</strong> BiDi (Bidirectional) control characters like Left-to-Right Mark (U+200E) or Right-to-Left Mark (U+200F) can manipulate text display order.
              </div>
              <div>
                <strong>Example:</strong> A URL containing RTL override characters can make text appear in reverse order, hiding malicious domains within legitimate-looking URLs.
              </div>
              <div>
                <strong>Detection:</strong> The tool identifies all directional marks and BiDi control characters, analyzing them for potential exploitation patterns.
              </div>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Format Character Hiding" variant="container">
            <SpaceBetween size="s">
              <div>
                <strong>Attack scenario:</strong> Soft hyphens (U+00AD), word joiners (U+2060), and other format characters can be used to hide information or create steganographic channels.
              </div>
              <div>
                <strong>Use cases for detection:</strong>
                <ul style={{ marginTop: '8px' }}>
                  <li>Identifying AI-generated content fingerprints</li>
                  <li>Detecting data exfiltration attempts</li>
                  <li>Uncovering hidden tracking mechanisms</li>
                  <li>Finding covert communication channels</li>
                </ul>
              </div>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Homograph Attack with Hidden Characters" variant="container">
            <SpaceBetween size="s">
              <div>
                <strong>Combined attack:</strong> Attackers may combine invisible characters with homoglyphs (look-alike characters) for even more sophisticated phishing.
              </div>
              <div>
                <strong>Example:</strong>{' '}
                <code>http://www.аpple.com</code> (uses Cyrillic 'а' + invisible characters)
              </div>
              <div>
                The tool detects both the non-ASCII homoglyph characters and any hidden invisible characters, providing comprehensive protection.
              </div>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Risk Levels Explained</Header>}>
        <SpaceBetween size="m">
          <div>
            <Box variant="h3">Safe (Score: 80-100)</Box>
            <div>
              The URL appears legitimate with no obvious security issues. However, always verify the source and use caution.
            </div>
          </div>

          <div>
            <Box variant="h3">Suspicious (Score: 50-79)</Box>
            <div>
              The URL has some concerning characteristics but may be legitimate. Exercise caution and verify before visiting.
            </div>
          </div>

          <div>
            <Box variant="h3">Dangerous (Score: 0-49)</Box>
            <div>
              The URL shows multiple red flags indicating potential phishing or malicious intent. Avoid clicking unless you're certain it's safe.
            </div>
          </div>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Best Practices for Invisible Character Detection</Header>}>
        <ul>
          <li><strong>Always analyze URLs before clicking:</strong> Paste URLs into this tool to check for hidden characters, especially from emails or untrusted sources</li>
          <li><strong>Copy and paste carefully:</strong> Invisible characters can be copied along with visible text, potentially bypassing visual inspection</li>
          <li><strong>Check URLs from AI tools:</strong> AI-generated content may contain invisible character fingerprints that could be used for tracking</li>
          <li><strong>Be cautious with shortened URLs:</strong> URL shorteners can hide invisible characters in the original destination</li>
          <li><strong>Verify domain spelling:</strong> Invisible characters combined with similar-looking letters create highly convincing phishing URLs</li>
          <li><strong>Don't rely on visual inspection alone:</strong> Invisible characters are, by definition, invisible—automated detection is essential</li>
          <li><strong>Type URLs manually when possible:</strong> This bypasses any hidden characters that might be in copied text</li>
          <li><strong>Use the tool's "Try Suspicious Example":</strong> See how invisible characters appear in analysis to understand the threat</li>
        </ul>
      </Container>

      <Container header={<Header variant="h2">What This Tool Does & Doesn't Do</Header>}>
        <SpaceBetween size="s">
          <div>
            <Box variant="h3">✅ What it DOES:</Box>
            <ul>
              <li>Detects all invisible Unicode characters in URLs (zero-width spaces, directional marks, format characters, etc.)</li>
              <li>Performs 21+ security pattern checks for steganography, normalization attacks, and character substitution</li>
              <li>Shows exact position and type of each invisible character</li>
              <li>Identifies homograph attacks using look-alike characters</li>
              <li>Provides detailed security risk assessment with severity levels</li>
              <li>Works completely offline—no data leaves your device</li>
            </ul>
          </div>
          <div>
            <Box variant="h3">❌ What it DOESN'T do:</Box>
            <ul>
              <li>Check if a legitimate-looking URL has been compromised</li>
              <li>Access real-time threat databases or reputation services</li>
              <li>Analyze the actual content of the destination website</li>
              <li>Detect all types of phishing (only invisible character and homoglyph-based attacks)</li>
              <li>Replace comprehensive security software or antivirus protection</li>
            </ul>
          </div>
          <div>
            <strong>Remember:</strong> This tool specializes in invisible character detection. Always use multiple layers of security and trust your instincts. If something seems suspicious, it probably is.
          </div>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Additional Resources</Header>}>
        <ul>
          <li>
            <Link external href="https://www.unicode.org/charts/">
              Unicode Character Database - Official Unicode Charts
            </Link>
          </li>
          <li>
            <Link external href="https://www.unicode.org/reports/tr36/">
              Unicode Technical Report #36: Unicode Security Considerations
            </Link>
          </li>
          <li>
            <Link external href="https://www.phishing.org/">
              Anti-Phishing Working Group (APWG)
            </Link>
          </li>
          <li>
            <Link external href="https://developer.mozilla.org/en-US/docs/Web/Security">
              MDN Web Security Guide
            </Link>
          </li>
        </ul>
      </Container>
    </SpaceBetween>
  );
};

